import { useEffect, useState } from 'react'
import { useRouteMatch } from 'react-router'

import { TreeNode } from 'common/components/catalog/Catalog'
import ErrorPage from 'common/components/error/ErrorPage'
import Spinner from 'common/components/spinner'
import { useAppSelector } from 'common/hooks/redux'
import Filters from 'features/personalization/components/report/Filters'

import useGetCustomerAttributesQuery from '../api/queries/useGetCustomerAttributesQuery'
import useGetFiltersQuery from '../api/queries/useGetFilters'
import useGetGraphsQuery from '../api/queries/useGetGraphs'
import useGetPersonalizationsQuery from '../api/queries/useGetPersonalizationsQuery'
import useGetVariantsQuery from '../api/queries/useGetVariants'
import Page from '../components/Page'

import Graphs from './components/graphs/Graphs'
import VariantTable from './components/VariantTable'
import Header from './Header'
import { PersonalizationInfo } from './interfaces'

const PersonalizationReport = () => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const match = useRouteMatch<{ id: string }>()
  const personalizationId = match.params.id

  const filtersQuery = useGetFiltersQuery()
  const productCategories = filtersQuery.data?.productCategories

  const personalizationsQuery = useGetPersonalizationsQuery()
  const currentPersonalization: PersonalizationInfo | undefined =
    personalizationsQuery.data?.find(
      (personalization) => personalization.id === personalizationId
    )
  // We use the first channel for now until we support multiple channels
  const channelId = currentPersonalization?.channels[0]

  const graphsQuery = useGetGraphsQuery({ channelId, personalizationId })
  const variantsQuery = useGetVariantsQuery(personalizationId)
  const customerAttributesQuery = useGetCustomerAttributesQuery({
    accountId,
  })

  // Will filter the variants on the frontend until we wire up the backend calling
  const filteredVariants = variantsQuery.data?.variants.filter(
    (variant) => variant.channelType === currentPersonalization?.channels[0]
  )

  const [selectedCustomerAttributes, setSelectedCustomerAttributes] =
    useState<TreeNode>(customerAttributesQuery.data ?? {})

  useEffect(() => {
    setSelectedCustomerAttributes(customerAttributesQuery.data ?? {})
  }, [customerAttributesQuery.data])

  const [selectedProductCategories, setSelectedProductCategories] =
    useState<TreeNode>(productCategories ?? {})

  useEffect(() => {
    setSelectedProductCategories(productCategories ?? {})
  }, [productCategories])

  const getState = () => {
    if (
      personalizationsQuery.isLoading ||
      filtersQuery.isLoading ||
      graphsQuery.isLoading ||
      variantsQuery.isLoading ||
      customerAttributesQuery.isLoading
    ) {
      return 'loading'
    } else if (
      personalizationsQuery.isError ||
      filtersQuery.isError ||
      graphsQuery.isError ||
      variantsQuery.isError ||
      customerAttributesQuery.isError
    ) {
      return 'error'
    } else {
      return 'success'
    }
  }
  const state = getState()

  return (
    <>
      {
        {
          success: (
            <>
              <Header
                trainingContent={currentPersonalization?.trainingContent}
              />

              <Page>
                <div className="flex flex-col flex-1 w-full bg-coolGray-50 gap-8 px-6">
                  <Filters
                    useCase={currentPersonalization?.useCase}
                    channels={currentPersonalization?.channels}
                    customerAttributes={customerAttributesQuery.data ?? {}}
                    selectedCustomerAttributes={
                      selectedCustomerAttributes ?? {}
                    }
                    onClickCustomerAttributes={(selected: TreeNode) => {
                      setSelectedCustomerAttributes(selected)
                    }}
                    productCategories={productCategories ?? {}}
                    selectedProductCategories={selectedProductCategories ?? {}}
                    onClickProductCategories={(selected: TreeNode) => {
                      setSelectedProductCategories(selected)
                    }}
                  />
                  <Graphs
                    channelId={channelId}
                    personalizationId={personalizationId}
                  />
                  <VariantTable
                    count={variantsQuery.data?.count ?? 0}
                    data={filteredVariants ?? []}
                  />
                </div>
              </Page>
            </>
          ),
          loading: (
            <div className="w-full">
              <Spinner data-testid="contents-loader" />
            </div>
          ),
          error: <ErrorPage />,
        }[state]
      }
    </>
  )
}

export default PersonalizationReport
